import axios from "axios";

import { userLogout, updateAccountSuccess } from "./user";
import { getHeader, getHeaderWithToken, getAuthHeader } from "../headers";

// ===================================================================================
//  GET REVIEWER (ADMIN)
// ===================================================================================
export const reviewerAdminLoading = () => {
	return {
		type: "REVIEWER_ADMIN_IS_LOADING"
	};
};

export const reviewerAdminError = err => {
	return {
		type: "REVIEWER_ADMIN_HAS_ERROR",
		err
	};
};

export const reviewerAdminSuccess = data => {
	return {
		type: "REVIEWER_ADMIN_SUCCESS",
		data
	};
};

export function getReviewerByAdmin(id, token) {
	return dispatch => {
		dispatch(reviewerAdminLoading());

		return axios
			.get(process.env.REACT_APP_API_URL + "reviewer/account?id=" + id, {
				headers: getHeaderWithToken(token)
			})
			.then(response => {
				if (response.data.status && response.data.status !== 200) {
					dispatch(reviewerAdminError(response.data.errors));
					return Promise.resolve(response);
				} else {
					dispatch(reviewerAdminSuccess(response));
					return Promise.resolve(response);
				}
			})
			.catch(err => {
				dispatch(reviewerAdminError(err.response.data.message));
			});
	};
}

// ===================================================================================
//  GET REVIEWER (USER)
// ===================================================================================
export const reviewerLoading = () => {
	return {
		type: "REVIEWER_IS_LOADING"
	};
};

export const reviewerError = err => {
	return {
		type: "REVIEWER_HAS_ERROR",
		err
	};
};

export const reviewerSuccess = () => {
	return {
		type: "REVIEWER_SUCCESS"
	};
};

export function getReviewer() {
	return (dispatch, getState) => {
		dispatch(reviewerLoading());

		return axios
			.get(process.env.REACT_APP_API_URL + "reviewer/", {
				headers: getAuthHeader(getState().user)
			})
			.then(response => {
				if (response.data.status === 403) {
					dispatch(userLogout("reviewer"));
				} else if (response.data.status && response.data.status !== 200) {
					dispatch(reviewerError(response.data.errors));
					return Promise.resolve(response);
				} else {
					dispatch(reviewerSuccess());
					dispatch(updateAccountSuccess(response.data.reviewer));
					return Promise.resolve(response);
				}
			})
			.catch(err => {
				dispatch(reviewerError(err.response.data.message));
			});
	};
}

// ===================================================================================
//  GENERATE PASSWORD
// ===================================================================================
export const passwordGenerateLoading = () => {
	return {
		type: "PASSWORD_GENERATE_LOADING"
	};
};

export const passwordGenerateError = err => {
	return {
		type: "PASSWORD_GENERATE_ERROR",
		err
	};
};

export const passwordGenerateSuccess = message => {
	return {
		type: "PASSWORD_GENERATE_SUCCESS",
		message
	};
};

export const passwordGenerateClearStatus = () => {
	return {
		type: "PASSWORD_GENERATE_CLEAR_STATUS"
	};
};

/**
 * Request that the API generate a password for this reviewer
 * @param id user's id
 * @param token user's token is passed in because it could be an admin or a user
 */
export function generateReviewerPassword(id, token) {
	return dispatch => {
		dispatch(passwordGenerateLoading());

		return axios
			.post(
				process.env.REACT_APP_API_URL + "reviewer/generate-password",
				{ id: id },
				{ headers: getHeaderWithToken(token) }
			)
			.then(response => {
				// console.log(response)
				if (response.data.status !== 200) {
					dispatch(passwordGenerateError(response.data.errors));
					return Promise.resolve(response);
				}

				dispatch(passwordGenerateSuccess(response.data.message));
				return Promise.resolve(response);
			})
			.catch(err => {
				dispatch(passwordGenerateError({ general: err }));
			});
	};
}

// ===================================================================================
//  UPDATE (PERSONAL)
// ===================================================================================
export const updatePersonalFieldsLoading = () => {
	return {
		type: "UPDATE_PERSONAL_FIELDS_LOADING"
	};
};

export const updatePersonalFieldsError = err => {
	return {
		type: "UPDATE_PERSONAL_FIELDS_ERROR",
		err
	};
};

export const updatePersonalFieldsSuccess = message => {
	return {
		type: "UPDATE_PERSONAL_FIELDS_SUCCESS",
		message
	};
};

export const updatePersonalFieldsClearStatus = () => {
	return {
		type: "UPDATE_PERSONAL_FIELDS_CLEAR_STATUS"
	};
};

/**
 * Update review personal info fields
 * @param data    User data object
 * @param isAdmin Use admin token (or reviewer)
 */
export function updatePersonalInfoFields(data, isAdmin = false) {
	return (dispatch, getState) => {
		dispatch(updatePersonalFieldsLoading());

		return axios
			.post(process.env.REACT_APP_API_URL + "reviewer/update-personal-fields", data, {
				headers: getAuthHeader(getState().user, isAdmin)
			})
			.then(response => {
				if (response.data.status === 403) {
					dispatch(userLogout("admin"));
				} else if (response.data.status !== 200) {
					dispatch(updatePersonalFieldsError(response.data.errors));
					return Promise.resolve(response);
				}

				dispatch(updatePersonalFieldsSuccess(response.data.message));
				dispatch(updateAccountSuccess(response.data.reviewer));
				return Promise.resolve(response);
			})
			.catch(err => {
				dispatch(updatePersonalFieldsError({ general: err }));
			});
	};
}

// ===================================================================================
//  UPDATE (IMAGE)
// ===================================================================================
export const updateReviewerImageLoading = () => {
	return {
		type: "UPDATE_REVIEWER_IMAGE_LOADING"
	};
};

export const updateReviewerImageError = err => {
	return {
		type: "UPDATE_REVIEWER_IMAGE_ERROR",
		err
	};
};

export const updateReviewerImageSuccess = message => {
	return {
		type: "UPDATE_REVIEWER_IMAGE_SUCCESS",
		message
	};
};

export const updateReviewerImageClearStatus = () => {
	return {
		type: "UPDATE_REVIEWER_IMAGE_CLEAR_STATUS"
	};
};

/**
 * Update reviewer profile image
 * @param data    User data object
 * @param isAdmin Use admin token (or reviewer)
 */
export function updateReviewerImage(data, isAdmin = false) {
	return (dispatch, getState) => {
		dispatch(updateReviewerImageLoading());

		return axios
			.post(process.env.REACT_APP_API_URL + "reviewer/update-profile-image", data, {
				headers: getAuthHeader(getState().user, isAdmin)
			})
			.then(response => {
				if (response.data.status === 403) {
					dispatch(userLogout("reviewer"));
				} else if (response.data.status !== 200) {
					dispatch(updateReviewerImageError(response.data.errors));
					return Promise.reject(response.data.errors);
				}

				dispatch(updateReviewerImageSuccess(response.data.message));
				dispatch(updateAccountSuccess(response.data.reviewer));
				return Promise.resolve(response);
			})
			.catch(err => {
				dispatch(updateReviewerImageError(err));
			});
	};
}

// ===================================================================================
//  GET REVIEWER OPTIONS
// ===================================================================================
export const reviewerOptionsIsLoading = () => {
	return {
		type: "REVIEWER_OPTIONS_IS_LOADING"
	};
};

export const reviewerOptionsHasError = err => {
	return {
		type: "REVIEWER_OPTIONS_HAS_ERROR",
		hasErrored: true,
		error: err
	};
};

export const reviewerOptionsSuccess = options => {
	return {
		type: "REVIEWER_OPTIONS_SUCCESS",
		options
	};
};

export function getReviewerOptions() {
	return dispatch => {
		dispatch(reviewerOptionsIsLoading());

		return axios
			.get(process.env.REACT_APP_API_URL + "reviewer/options", { headers: getHeader() })
			.then(response => {
				dispatch(reviewerOptionsSuccess(response.data.options));
			})
			.catch(err => {
				// console.log('getSiteInfo error:', err)
				dispatch(reviewerOptionsHasError(err));
			});
	};
}

// ===================================================================================
//  GET REVIEWER ACTIVE REVIEWS
// ===================================================================================
export const reviewerActiveReviewsIsLoading = () => {
	return {
		type: "REVIEWER_ACTIVE_REVIEWS_IS_LOADING"
	};
};

export const reviewerActiveReviewsHasError = err => {
	return {
		type: "REVIEWER_ACTIVE_REVIEWS_HAS_ERROR",
		error: err
	};
};

export const reviewerActiveReviewsSuccess = data => {
	return {
		type: "REVIEWER_ACTIVE_REVIEWS_SUCCESS",
		data
	};
};

export const saveReviewer = data => {
	return {
		type: "SAVE_REVIEWER",
		data
	};
};

export function DispatchSaveReviewer(data) {
	return dispatch => {
		dispatch(saveReviewer(data));
	};
}

export function getActiveReviewerReviews() {
	return (dispatch, getState) => {
		dispatch(reviewerActiveReviewsIsLoading());

		return axios
			.get(process.env.REACT_APP_API_URL + "reviewer/active-reviews", {
				headers: getAuthHeader(getState().user)
			})
			.then(response => {
				dispatch(reviewerActiveReviewsSuccess(response));
			})
			.catch(err => {
				dispatch(reviewerActiveReviewsHasError(err));
			});
	};
}



// WEBPACK FOOTER //
// ./src/store/actions/reviewers.js